import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface AuthState {
  isLoggedIn: boolean;
  token: string | null;
  role: string | null;
  name: string | null;
}

interface AuthContextType extends AuthState {
  login: (token: string, role: string, name?: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [auth, setAuth] = useState<AuthState>(() => {
    const token = localStorage.getItem('token');
    return {
      isLoggedIn: !!token,
      token,
      role: localStorage.getItem('role'),
      name: localStorage.getItem('name'),
    };
  });

  const login = useCallback((token: string, role: string, name?: string) => {
    localStorage.setItem('token', token);
    localStorage.setItem('role', role);
    if (name) localStorage.setItem('name', name);
    setAuth({ isLoggedIn: true, token, role, name: name || null });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('name');
    localStorage.removeItem('adminToken');
    setAuth({ isLoggedIn: false, token: null, role: null, name: null });
  }, []);

  // Sync across tabs
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'token') {
        const token = localStorage.getItem('token');
        setAuth({
          isLoggedIn: !!token,
          token,
          role: localStorage.getItem('role'),
          name: localStorage.getItem('name'),
        });
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return (
    <AuthContext.Provider value={{ ...auth, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
